/**
 * Translating what the API says.
 *
 * `axiosInstance` pipes backend text straight onto the screen, so without this an Arabic user gets
 * an Arabic UI that throws English errors at them on every validation failure — the most constant
 * reminder that a translation was only skin-deep.
 *
 * Two very different kinds of text arrive here, and the distinction is the whole design:
 *
 * 1. **Text this SPA authored** — the HTTP-status descriptions and network-failure lines in
 *    `getErrorParts`. These are ours, they are a closed set, and they are fully translated.
 * 2. **Text the backend authored** — .NET `{message,title}`, NestJS `{message,statusCode}`,
 *    payroll `{error,details}`. Open-ended, changes without warning, and often quotes tenant data.
 *    Only the recognised ones are translated; everything else falls back to the server's English.
 *
 * Falling back rather than swallowing matters: an untranslated but *accurate* message is far more
 * useful than a generic "an error occurred" that hides which guard actually refused. Full
 * `Accept-Language` support in UM/EM/PM is deliberately out of scope for row 4.3 —
 * docs/43-I18N-ARABIC-DESIGN.md §11.
 */

import i18next from 'i18next';

const NS = 'errors';

/** Is a catalogue available yet? Errors can fire before i18next finishes booting. */
const canTranslate = (): boolean => i18next.isInitialized;

const t = (key: string, fallback: string): string => {
  if (!canTranslate()) return fallback;
  const value = i18next.t(`${NS}:${key}`, { defaultValue: fallback });
  return typeof value === 'string' ? value : fallback;
};

// ── SPA-authored text ────────────────────────────────────────────────────────

export const networkErrorText = (kind: 'timeout' | 'unreachable'): { title: string; description: string } =>
  kind === 'timeout'
    ? {
        title: t('network.timeout.title', 'Request timed out'),
        description: t('network.timeout.description', 'Please check your connection and try again.'),
      }
    : {
        title: t('network.unreachable.title', 'Cannot reach server'),
        description: t(
          'network.unreachable.description',
          'Check your internet connection or try again shortly.',
        ),
      };

/**
 * Our own description for an HTTP status.
 *
 * Returns null for statuses we have no line for, so the caller keeps its existing fallback rather
 * than this module inventing one.
 */
export const statusText = (status: number): string | null => {
  const known: Record<number, [string, string]> = {
    400: ['status.400', 'Invalid request — please check your input and try again.'],
    401: ['status.401', 'Your session has expired. Please log in again.'],
    403: ['status.403', "You don't have permission to perform this action."],
    404: ['status.404', 'The requested resource was not found.'],
    408: ['status.408', 'Request timed out. Please try again.'],
    409: ['status.409', 'A conflict occurred. The data may have been modified by someone else.'],
    422: ['status.422', 'The submitted data is invalid. Please review and try again.'],
    429: ['status.429', 'Too many requests. Please wait a moment and try again.'],
    500: ['status.500', 'Something went wrong on the server. Please try again later.'],
    502: ['status.502', 'Server gateway error. Please try again in a moment.'],
    503: ['status.503', 'Service temporarily unavailable. Please try again later.'],
    504: ['status.504', 'Server took too long to respond. Please try again.'],
  };
  const entry = known[status];
  return entry ? t(entry[0], entry[1]) : null;
};

export const genericErrorText = (status: number): string =>
  status >= 500
    ? t('generic.server', 'A server error occurred. Please try again later.')
    : t('generic.unexpected', 'An unexpected error occurred. Please try again.');

export const sessionExpiredText = (): string =>
  t('session.expired', 'Session expired. Please login again.');

// ── Backend-authored text ────────────────────────────────────────────────────

/**
 * Backend messages recognised well enough to translate, keyed by a normalised form of the English.
 *
 * Normalisation lowercases, strips trailing punctuation and collapses whitespace, because the same
 * message reaches us with and without a full stop from different services.
 *
 * **Only exact, whole-message matches are translated.** Substring matching was considered and
 * rejected: backend messages routinely embed tenant data ("Employee Ali Khan already has a leave
 * request…"), and a partial match would translate the frame while stranding the data, producing a
 * sentence that is neither language. An exact match is safe precisely because it carries no data.
 *
 * Grow this list as modules are migrated — a session that meets a recurring backend message in its
 * module adds the row here.
 */
const KNOWN_MESSAGES: Record<string, string> = {
  // Permission / auth — the generic refusals `axiosInstance` already recognises as saying nothing
  forbidden: 'refusal.generic',
  'forbidden resource': 'refusal.generic',
  denied: 'refusal.generic',
  'access denied': 'refusal.generic',
  'access is denied': 'refusal.generic',
  unauthorized: 'refusal.generic',
  unauthorised: 'refusal.generic',
  'not authorized': 'refusal.generic',
  'not authorised': 'refusal.generic',
  'no permission': 'refusal.generic',
  'permission denied': 'refusal.generic',
  'insufficient permission': 'refusal.generic',
  'insufficient permissions': 'refusal.generic',
  'you do not have permission': 'refusal.generic',
  "you don't have permission": 'refusal.generic',
  'you do not have permission to perform this action': 'refusal.generic',
  "you don't have permission to perform this action": 'refusal.generic',
  'you are not allowed to perform this action': 'refusal.generic',
  'you are not allowed to access this resource': 'refusal.generic',

  // Common validation shapes seen across UM / EM / PM
  'invalid credentials': 'auth.invalidCredentials',
  'user not found': 'auth.userNotFound',
  'invalid token': 'auth.invalidToken',
  'token expired': 'auth.tokenExpired',
  'validation failed': 'validation.failed',
  'record not found': 'notFound.record',
  'employee not found': 'notFound.employee',
  'organization not found': 'notFound.organization',
  'an error occurred while saving the entity changes. see the inner exception for details':
    'generic.server',
};

const normalizeMessage = (text: string): string =>
  text.trim().replace(/\s+/g, ' ').replace(/[.!]+$/, '').toLowerCase();

/**
 * Translate a backend message if it is one we recognise, otherwise null.
 *
 * Null means "keep the server's own text" — never "show nothing".
 */
export const translateServerMessage = (text: string | null | undefined): string | null => {
  if (!text || !canTranslate()) return null;
  const key = KNOWN_MESSAGES[normalizeMessage(text)];
  if (!key) return null;
  const value = i18next.t(`${NS}:${key}`, { defaultValue: '' });
  return typeof value === 'string' && value ? value : null;
};

/** Translate when recognised, otherwise hand back exactly what the server said. */
export const localizeServerMessage = <T extends string | null | undefined>(text: T): string | T =>
  translateServerMessage(text) ?? text;
